import { useCallback, useEffect, useRef, useState } from "react";
import { api_getGameState } from "../axios";

/**
 * Reads the live game state (inning / B-S-O / batter / pitcher / pitch counts)
 * for the linked game from the backend. Read-only and fully decoupled from the
 * socket submit path: a failure here NEVER throws and NEVER blocks recording —
 * the HUD just degrades to "現況無法取得".
 *
 * Refresh policy (spec §4.5): auto-fetch when a game is linked, then refresh on
 * demand — the caller invokes refresh() after each send and from a manual
 * button. `fetcher` is injectable for tests.
 */
export const useGameState = ({ game, fetcher = api_getGameState } = {}) => {
  const [state, setState] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultKey, setResultKey] = useState("");
  // `game` is the link-form triple { game_no, league_id, year } (the backend
  // resolves it to the internal game_id). gameKey is its stable identity.
  const gameKey = game ? [game.year, game.league_id, game.game_no].join("/") : "";
  // Mirror the latest key + payload into refs so an in-flight (or late-invoked)
  // refresh can tell whether it still matches the live game.
  const keyRef = useRef(gameKey);
  keyRef.current = gameKey;
  const gameRef = useRef(game);
  gameRef.current = game;
  // Monotonic token so that among concurrent same-game refreshes (auto + on-send
  // firing together) only the latest response wins.
  const reqRef = useRef(0);

  const refresh = useCallback(async () => {
    // Bail if no game, or this is a stale closure invoked after the game already
    // changed (e.g. an on-send socket ack landing after the user switched game).
    if (!gameKey || gameKey !== keyRef.current) return;
    const reqId = ++reqRef.current;
    const fresh = () => gameKey === keyRef.current && reqId === reqRef.current;
    setLoading(true);
    try {
      const res = await fetcher(gameRef.current);
      if (!fresh()) return; // game changed or a newer same-game request superseded us
      if (res && res.status && res.data) {
        setState(res.data);
        setError(false);
      } else {
        setError(true); // keep last state (if any); HudRail flags it as stale
      }
    } catch {
      if (fresh()) setError(true);
    } finally {
      if (fresh()) setLoading(false);
    }
  }, [gameKey, fetcher]);

  useEffect(() => {
    // A different game must not inherit the prior game's last successful HUD.
    // Manual refreshes of the same game still keep their last good state.
    setResultKey(gameKey);
    setState(null);
    setError(false);
    if (gameKey) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [gameKey, refresh]);

  // Hide previous-game values during the render before the effect resets them.
  const current = resultKey === gameKey;
  return { state: current ? state : null, error: current && error, loading: current ? loading : !!gameKey, refresh };
};
